import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { DamageReason } from 'src/types/damage-reason';
import {
  IStationeryDamageDispatchDetailedItem,
  IStationeryDamageDispatchListItem,
} from 'src/types/stationery-damage-dispatch';

import axios from '../../utils/axios';

interface IInitialState {
  status: 'idle' | 'loading' | 'success' | 'failure';
  error: string | undefined;
  damageDispatches: {
    count: number;
    page: number;
    pageSize: number;
    pageCount: number;
    items: IStationeryDamageDispatchListItem[];
  };
  damageDispatch: IStationeryDamageDispatchDetailedItem | null;
}

const initialState: IInitialState = {
  status: 'idle',
  error: undefined,
  damageDispatches: {
    count: 0,
    page: 0,
    pageSize: 0,
    pageCount: 0,
    items: [],
  },
  damageDispatch: null,
};

const stationeryDamageDispatchSlice = createSlice({
  name: 'stationeryDamageDispatch',
  initialState,
  reducers: {
    removeStationeryDamageDispatch: (state, action) => {
      const { damageDispatchId } = action.payload;

      const index = state.damageDispatches.items.findIndex(
        (item) => item._id === damageDispatchId
      );

      if (index > -1) {
        action.payload.index = index;
        state.damageDispatches.items = state.damageDispatches.items.filter(
          (item) => item._id !== damageDispatchId
        );
      }
    },
    rollbackStationeryDamageDispatchDeletion: (state, action) => {
      const { item, index } = action.payload;

      state.damageDispatches.items.splice(index, 0, item);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getStationeryDamageDispatches.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStationeryDamageDispatches.fulfilled, (state, action) => {
        state.status = 'success';
        state.damageDispatches = action.payload;
      })
      .addCase(getStationeryDamageDispatches.rejected, (state, action) => {
        state.damageDispatches = initialState.damageDispatches;
        state.status = 'failure';
        state.error = action.error.message;
      })
      .addCase(getStationeryDamageDispatchById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(getStationeryDamageDispatchById.fulfilled, (state, action) => {
        state.status = 'success';
        state.damageDispatch = action.payload;
        state.error = initialState.error;
      })
      .addCase(getStationeryDamageDispatchById.rejected, (state, action) => {
        state.damageDispatch = initialState.damageDispatch;
        state.status = 'failure';
        state.error = action.error.message;
      });
  },
});

export default stationeryDamageDispatchSlice.reducer;

export const {
  removeStationeryDamageDispatch,
  rollbackStationeryDamageDispatchDeletion,
} = stationeryDamageDispatchSlice.actions;

export const getStationeryDamageDispatches = createAsyncThunk(
  'stationeryDamageDispatch/paginated',
  async (params: {
    page: number;
    limit: number;
    generalSearch?: string | null;
    startDate?: Date | null;
    endDate?: Date | null;
    sortBy?: string | null;
    sort?: 'asc' | 'desc' | null;
  }) => {
    const { page, limit, generalSearch, startDate, endDate, sortBy, sort } =
      params;

    try {
      const response = await axios.get(
        `stationery/damage-dispatch?page=${page + 1}&limit=${limit}&generalSearch=${
          generalSearch ?? ''
        }&startDate=${startDate ? startDate.toISOString() : ''}&endDate=${
          endDate ? endDate.toISOString() : ''
        }&sortBy=${sortBy}&sort=${sort ?? ''}`
      );

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const getStationeryDamageDispatchById = createAsyncThunk(
  'stationeryDamageDispatch/getById',
  async (params: { itemID: string }) => {
    try {
      const { itemID } = params;
      const response = await axios.get(`stationery/damage-dispatch/${itemID}`);

      return response.data.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  }
);

export const deleteStationeryDamageDispatch = createAsyncThunk(
  'stationeryDamageDispatch/delete',
  async (
    params: { item: IStationeryDamageDispatchListItem; index: number },
    { dispatch }
  ) => {
    const { item, index } = params;

    try {
      const response = await axios.delete(
        `stationery/damage-dispatch/${item._id}`
      );
      return response.data.data;
    } catch (err) {
      dispatch(rollbackStationeryDamageDispatchDeletion({ item, index }));
      throw err;
    }
  }
);

export interface TStationeryDamageDispatchItemInput {
  stationeryItem: string;
  quantity: number;
  damageReason: DamageReason;
}

export interface TCreateStationeryDamageDispatch {
  damageDate: Date;
  remarks: string | null;
  items: TStationeryDamageDispatchItemInput[];
}

export const createStationeryDamageDispatch = createAsyncThunk(
  'stationeryDamageDispatch/create',
  async (params: { damageDispatchTransaction: TCreateStationeryDamageDispatch }) => {
    const { damageDispatchTransaction } = params;
    const response = await axios.post(
      'stationery/damage-dispatch',
      damageDispatchTransaction
    );
    return response.data.data;
  }
);
